import type { ReactElement } from "react";
import { isLiftableAction } from "odyssey-shared";
import { Button, EmptyState } from "../../ui/kit/index.ts";
import { say } from "../../words.ts";
import type { ListedRow } from "./client.ts";
import type { DeskSnapshot } from "./desk.ts";
import { historyWhat, soonestFirst } from "./layout.ts";
import { StandingRow } from "./player-tab.tsx";
import { shortWhenWords } from "./words.ts";

/**
 * The Moderation window's In force and History tabs (ADR-056's amendment of 2026-10-06, decisions 9 and 14; the
 * design's `moderation-window` card, a visual reference), over the desk's two reads of `GET /moderation`: every ban
 * and freeze that stands, across all families, soonest to end first, each ended from its own row and each opening
 * its family on the Player tab, online or not; and everything recorded, newest first, with who did it. A row's End
 * is enabled where the gateway says this GM may end it (`mayEnd`) and disabled with the lift's sentence otherwise.
 */

/**
 * A list the gateway refused to read: its sentence, announced at once.
 *
 * @param props The `sentence`.
 * @returns The line.
 */
function ListRefused({ sentence }: { readonly sentence: string }): ReactElement {
  return (
    <p className="ot-moderation__refused" role="alert" data-moderation="list-error">
      {sentence}
    </p>
  );
}

/**
 * The In force tab.
 *
 * @param props The desk's `snapshot`; whether `lift` is offered; `now`; `onEnd`, run with a row's End;
 *   and `onOpen`, run with a family's name when its row is pressed.
 * @returns The tab's body.
 */
export function InForceTab({ snapshot, lifts, now, onEnd, onOpen }: { readonly snapshot: DeskSnapshot; readonly lifts: boolean; readonly now: number; readonly onEnd: (row: ListedRow) => void; readonly onOpen: (family: string) => void }): ReactElement {
  if (snapshot.listError !== null && !snapshot.standingRead) {
    return <ListRefused sentence={snapshot.listError} />;
  }
  if (snapshot.standingRead && snapshot.standing.length === 0) {
    return (
      <EmptyState glyph="check" title={say("moderation.window.inforceEmptyTitle")}>
        {say("moderation.window.inforceEmpty")}
      </EmptyState>
    );
  }
  return (
    <div className="ot-moderation__records" data-moderation="inforce">
      <p className="ot-moderation__muted ot-moderation__small ot-moderation__order">{say("moderation.window.inforceOrder")}</p>
      {soonestFirst(snapshot.standing).map((row) =>
        isLiftableAction(row.action) ? (
          <StandingRow key={row.id} row={{ id: row.id, action: row.action, issuer: row.issuer, until: row.until, reason: row.reason }} family={row.familyName} mayEnd={row.mayEnd} offered={lifts} now={now} onEnd={() => onEnd(row)} onOpen={() => onOpen(row.familyName)} />
        ) : null,
      )}
      <p className="ot-moderation__muted ot-moderation__small">{say("moderation.window.inforceNote")}</p>
    </div>
  );
}

/**
 * The History tab: a table of when, family, what and by, each row with what the player read or the staff's note
 * under it, and Show more while a page remains.
 *
 * @param props The desk's `snapshot`; `now`, which a row's moment is written against; and `onMore`.
 * @returns The tab's body.
 */
export function HistoryTab({ snapshot, now, onMore }: { readonly snapshot: DeskSnapshot; readonly now: number; readonly onMore: () => void }): ReactElement {
  if (snapshot.listError !== null && snapshot.rows.length === 0) {
    return <ListRefused sentence={snapshot.listError} />;
  }
  if (!snapshot.loading && snapshot.rows.length === 0) {
    return (
      <>
        <p className="ot-moderation__muted ot-moderation__small ot-moderation__order">{say("moderation.window.historyOrder")}</p>
        <EmptyState glyph="check" title={say("moderation.window.historyEmptyTitle")}>
          {say("moderation.window.historyEmpty")}
        </EmptyState>
      </>
    );
  }
  return (
    <div className="ot-moderation__records" data-moderation="history">
      <p className="ot-moderation__muted ot-moderation__small ot-moderation__order">{say("moderation.window.historyOrder")}</p>
      <div role="table" aria-label={say("moderation.window.historyLabel")} className="ot-moderation__table ot-panel ot-ui-frame-panel" data-moderation="rows">
        <div role="row" className="ot-moderation__history-row ot-moderation__history-head">
          {[say("moderation.window.columns.when"), say("moderation.window.columns.family"), say("moderation.window.columns.what"), say("moderation.window.columns.by")].map((column) => (
            <span key={column} role="columnheader">
              {column}
            </span>
          ))}
        </div>
        {snapshot.rows.map((row) => (
          <div key={row.id} role="row" className="ot-moderation__history-item" data-moderation="row">
            <div className="ot-moderation__history-row">
              <span role="cell" className="ot-moderation__muted ot-moderation__small ot-moderation__clip">
                {shortWhenWords(row.at, now, true)}
              </span>
              <span role="cell" className="ot-moderation__clip">
                {row.familyName}
              </span>
              <span role="cell" className="ot-moderation__clip">
                {historyWhat(row)}
              </span>
              <span role="cell" className="ot-moderation__muted ot-moderation__small ot-moderation__clip">
                {row.issuer}
              </span>
            </div>
            <div className="ot-moderation__muted ot-moderation__small ot-moderation__clip ot-moderation__history-why">{row.action === "alert" || row.action === "lift" ? say("moderation.window.staffNote", { note: row.note ?? row.reason }) : say("moderation.window.readQuote", { family: row.familyName, reason: row.reason })}</div>
          </div>
        ))}
        {snapshot.next === null ? null : (
          <div className="ot-moderation__more">
            <Button onClick={onMore} disabled={snapshot.loading}>
              {say("moderation.window.more")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
